import { makeRequest, apiClient } from '../../../../services/apiClient';

// Detect 404 or "Not Found" errors robustly
const isNotFound = (err) => {
  if (!err) return false;
  const status = err?.response?.status;
  if (status === 404) return true;
  const msg = String(err?.message || err || '');
  return /Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg);
};

// Derive backend origin from axios baseURL or window origin
const getBackendOrigin = () => {
  try {
    const base = apiClient?.defaults?.baseURL || '';
    if (!base) return null;
    const u = new URL(base, window?.location?.origin);
    return u.origin; // e.g., http://localhost:8000
  } catch (e) {
    return null;
  }
};

/**
 * Fetch object type combinations per event.
 * Expected response shape (from backend):
 * {
 *   file_type: string,
 *   items: [ { combination: string[], count: number }, ...],
 *   validation: { events_processed, events_with_no_objects, referenced_object_ids, unknown_object_ids, consistency_ok }
 * }
 */
export const fetchObjectTypeCombinations = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  try {
    // Primary endpoint - KPI router is mounted at /api/kpi
    return await makeRequest('/kpi/object-type-combinations', { params }, 'object-type-combinations');
  } catch (err) {
    // Fallbacks for different mounts
    if (isNotFound(err)) {
      try {
        return await makeRequest('/analytics/object-type-combinations', { params }, 'object-type-combinations (fallback)');
      } catch (err2) {
        if (isNotFound(err2) && typeof window !== 'undefined' && window.location) {
          const origin = getBackendOrigin() || window.location.origin;
          const candidates = [
            `${origin}/object-type-combinations`,
            `${origin}/api/object-type-combinations`,
            `${origin}/api/v1/object-type-combinations`,
            `${origin}/v1/object-type-combinations`,
            `${origin}/analytics/object-type-combinations`,
            `${origin}/api/analytics/object-type-combinations`,
            `${origin}/api/v1/analytics/object-type-combinations`,
            `${origin}/v1/analytics/object-type-combinations`,
            `${origin}/gateway/api/object-type-combinations`,
            `${origin}/gateway/api/v1/object-type-combinations`,
            `${origin}/gateway/v1/object-type-combinations`,
            `${origin}/gateway/analytics/object-type-combinations`,
            `${origin}/gateway/api/analytics/object-type-combinations`,
          ];
          for (const url of candidates) {
            try {
              return await makeRequest(url, { params }, `object-type-combinations (${url})`);
            } catch (e2) {
              if (!isNotFound(e2)) throw e2;
            }
          }
        }
      }
    }
    // If none matched, rethrow original error
    throw err;
  }
};
